import { useEffect, useState } from "react";
import { GD } from "src/GD";
import { TPaymentChannel } from "../channelList/Channels";
import { useTranslation } from "react-i18next";

export interface IStatChannelOption {
	/** Value sent to overview/get as `channel_id`. */
	value: string;
	/** Human-readable channel name or merchant channel alias. */
	name: string;
}

/** Sentinel value for the "no filter" option (an empty string renders as "---" in Select). */
export const ALL_CHANNELS_VALUE = "all";

const isActiveChannel = (channel: TPaymentChannel) => channel.status !== "deleted" && channel.linked?.status !== "deleted";

/** Stable id used both as the dropdown value and as the overview/get filter key. */
const getChannelFilterId = (channel: TPaymentChannel): number => channel.linked?.id ?? channel.id;

const getChannelLabel = (channel: TPaymentChannel, fallback: (id: number) => string): string => {
	const alias = channel.linked?.alias?.trim();
	if (alias) return alias;
	if (channel.name?.trim()) return channel.name.trim();
	return fallback(getChannelFilterId(channel));
};

const getChannelsFromResponse = (data: unknown): TPaymentChannel[] => {
	if (!data) return [];
	if (Array.isArray(data)) return data as TPaymentChannel[];
	const obj = data as { channels?: unknown; data?: { channels?: unknown } | unknown[] };
	if (Array.isArray(obj.channels)) return obj.channels as TPaymentChannel[];
	if (Array.isArray(obj.data)) return obj.data as TPaymentChannel[];
	if (obj.data && Array.isArray((obj.data as { channels?: unknown }).channels)) return (obj.data as { channels: TPaymentChannel[] }).channels;
	return [];
};

/** Resolve the merchant ids whose channels make up this target's statistics. */
const loadMerchantIds = async (client_id: number, type: "client" | "merchant"): Promise<number[]> => {
	if (type === "merchant") return [client_id];

	const res = await GD.REQ_API.request({ method: "merchants/list", data: { client_id } });
	const items = Array.isArray(res?.data) ? res.data : Array.isArray((res?.data as { items?: unknown })?.items) ? (res!.data as { items: unknown[] }).items : [];

	return items
		.map(item => (item && typeof item === "object" ? (item as { id?: unknown }).id : undefined))
		.filter((id): id is number => typeof id === "number" && id > 0);
};

/**
 * Loads the channels available to a dashboard target. Project dashboards use
 * the project-scoped channel list in one request; client and merchant dashboards
 * load their merchant-linked channels.
 */
export const useStatChannels = (client_id: number, type: "client" | "merchant" | "project") => {
	const { t, i18n } = useTranslation();
	const allChannelsOption: IStatChannelOption = { value: ALL_CHANNELS_VALUE, name: t("stats.allChannels") };
	const [options, setOptions] = useState<IStatChannelOption[]>([allChannelsOption]);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<IError>();

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			setBusy(true);
			setError(undefined);

			try {
				let responses: IAPIResponse[];
				if (type === "project") {
					responses = [await GD.REQ_API.request({ method: "payments/channels/list", data: {} })];
				} else {
					const merchantIds = await loadMerchantIds(client_id, type);
					if (cancelled) return;
					responses = await Promise.all(
						merchantIds.map(merchant_id => GD.REQ_API.request({ method: "payments/channels/merchant/list", data: { merchant_id } })),
					);
				}
				if (cancelled) return;

				const firstError = responses.find(res => res?.error)?.error;
				if (firstError && responses.every(res => res?.error)) {
					setError(firstError);
					setOptions([allChannelsOption]);
					return;
				}

				const byId = new Map<number, IStatChannelOption>();
				for (const res of responses) {
					for (const channel of getChannelsFromResponse(res?.data)) {
						if (!isActiveChannel(channel)) continue;
						const id = getChannelFilterId(channel);
						if (byId.has(id)) continue;
						byId.set(id, { value: String(id), name: getChannelLabel(channel, channelId => t("stats.channelNumber", { id: channelId })) });
					}
				}

				const sorted = Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
				setOptions([allChannelsOption, ...sorted]);
			} finally {
				if (!cancelled) setBusy(false);
			}
		};

		void load();

		return () => {
			cancelled = true;
		};
	}, [client_id, type, i18n.resolvedLanguage]);

	return { options, busy, error };
};
